import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Copy, Trash2, AtSign, Mail, Check, Key,
  Forward, Settings2, ChevronRight, AlertCircle, Send, Loader2, FolderOpen, GripVertical,
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * AliasSenderPreview — PROMPT 013
 *
 * Tiny live preview of the sender presentation used by the alias.
 * Renders one of:
 *   - `Display Name <alias@mail.quickinbox.app>`  (both filled)
 *   - `alias@mail.quickinbox.app`                 (no display name)
 *   - `Display Name <auto-generated address>`     (create mode, no address yet)
 *
 * Pure display, no validation — empty/whitespace input collapses the
 * angle-bracket part so the preview never shows `<>`.
 */
function AliasSenderPreview({ name, address, testId = "alias-sender-preview" }) {
  const trimmed = (name || "").trim();
  const addr = address || "";
  const previewText = trimmed
    ? (addr ? `${trimmed} <${addr}>` : `${trimmed} <auto-generated address>`)
    : (addr || "auto-generated@mail.quickinbox.app");
  return (
    <p
      className="text-xs text-zinc-500 font-mono mt-1.5 select-text break-all"
      data-testid={testId}
    >
      <span className="text-zinc-400 not-italic font-sans mr-1.5">Will appear as:</span>
      {previewText}
    </p>
  );
}

const GROUP_DOT_COLOR = {
  zinc: "bg-zinc-400", blue: "bg-blue-500", emerald: "bg-emerald-500",
  amber: "bg-amber-500", red: "bg-red-500", violet: "bg-violet-500",
  cyan: "bg-cyan-500", rose: "bg-rose-500",
};

import InboxGroups from "@/components/InboxGroups";

function SortableAliasRow({ alias, copiedId, onSelect, onCopy, onToggle, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: alias.alias_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 px-4 sm:px-6 py-4 hover:bg-[#F5F5F5] transition-colors cursor-pointer ${isDragging ? "shadow-lg bg-white rounded-lg" : ""}`}
      data-testid={`alias-item-${alias.alias_id}`}
      onClick={onSelect}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="p-1 rounded text-zinc-300 hover:text-zinc-500 cursor-grab active:cursor-grabbing shrink-0 touch-none"
        onClick={(e) => e.stopPropagation()}
        data-testid={`drag-handle-${alias.alias_id}`}
      >
        <GripVertical size={16} />
      </button>

      {/* Icon */}
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${alias.is_active ? "bg-blue-50" : "bg-[#F5F5F5]"}`}>
        <AtSign size={18} className={alias.is_active ? "text-blue-600" : "text-zinc-500"} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-[#111111] truncate">{alias.label}</p>
          {!alias.is_active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
          {alias.forward_enabled && alias.forward_to && (
            <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px] gap-1" variant="outline" data-testid={`forwarding-badge-${alias.alias_id}`}>
              <Forward size={10} /> Forwarding
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[11px] text-zinc-500 bg-[#F5F5F5] px-1.5 py-0.5 rounded border border-[#E5E5E5] truncate">
            {alias.alias_address}
          </span>
          <button
            onClick={onCopy}
            className="text-zinc-500 hover:text-[#555555] transition-colors shrink-0"
            data-testid={`copy-alias-${alias.alias_id}`}
          >
            {copiedId === alias.alias_id ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
          </button>
        </div>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-[11px] text-zinc-500 flex items-center gap-1">
            <Mail size={11} /> {alias.email_count || 0} emails
          </span>
          {alias.forward_to && (
            <span className="text-[11px] text-zinc-500 flex items-center gap-1 truncate max-w-[180px]">
              <Forward size={11} /> {alias.forward_to}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
        <Switch
          checked={alias.is_active}
          onCheckedChange={onToggle}
          data-testid={`toggle-alias-${alias.alias_id}`}
        />
        <button
          onClick={onSelect}
          className="p-1.5 rounded hover:bg-[#F5F5F5] text-zinc-500 hover:text-[#555555] transition-colors"
          data-testid={`config-alias-${alias.alias_id}`}
        >
          <Settings2 size={16} />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 rounded hover:bg-red-50 text-zinc-500 hover:text-red-500 transition-colors"
          data-testid={`delete-alias-${alias.alias_id}`}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

export default function AliasPage() {
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [claimCode, setClaimCode] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedAlias, setSelectedAlias] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [totalAliasCount, setTotalAliasCount] = useState(0);
  const [groupsVersion, setGroupsVersion] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = aliases.findIndex(a => a.alias_id === active.id);
    const newIndex = aliases.findIndex(a => a.alias_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = [...aliases];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    setAliases(reordered);
    try {
      await api.put("/aliases/reorder", {
        order: reordered.map((a, i) => ({ alias_id: a.alias_id, sort_order: i })),
      });
    } catch {
      toast.error("Failed to save order");
      fetchAliases();
    }
  };

  const fetchAliases = useCallback(async () => {
    try {
      const params = activeGroup ? `?group_id=${activeGroup}` : "";
      const { data } = await api.get(`/aliases${params}`);
      setAliases(data);
      if (!activeGroup) {
        setTotalAliasCount(data.length);
      }
    } catch {
      toast.error("Failed to load aliases");
    } finally {
      setLoading(false);
    }
  }, [activeGroup]);

  useEffect(() => { fetchAliases(); }, [fetchAliases]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.post("/aliases", { label: newLabel || undefined, description: newDesc || undefined });
      toast.success("Alias created");
      setNewLabel("");
      setNewDesc("");
      setDialogOpen(false);
      setTotalAliasCount((c) => c + 1);
      fetchAliases();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create alias");
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (alias) => {
    try {
      await api.put(`/aliases/${alias.alias_id}`, { is_active: !alias.is_active });
      setAliases((prev) => prev.map((a) =>
        a.alias_id === alias.alias_id ? { ...a, is_active: !a.is_active } : a
      ));
      toast.success(alias.is_active ? "Alias deactivated" : "Alias activated");
    } catch {
      toast.error("Failed to update alias");
    }
  };

  const handleDelete = async (aliasId) => {
    try {
      await api.delete(`/aliases/${aliasId}`);
      setAliases((prev) => prev.filter((a) => a.alias_id !== aliasId));
      setTotalAliasCount((c) => Math.max(0, c - 1));
      if (selectedAlias?.alias_id === aliasId) setSelectedAlias(null);
      toast.success("Alias deleted");
    } catch {
      toast.error("Failed to delete alias");
    }
  };

  const copyAddress = (address, aliasId) => {
    try {
      navigator.clipboard.writeText(address);
    } catch {
      const el = document.createElement("textarea");
      el.value = address;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopiedId(aliasId);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Copied to clipboard");
  };

  const handleClaimAlias = async () => {
    if (!claimCode.trim()) {
      toast.error("Enter a claim code");
      return;
    }
    setClaiming(true);
    try {
      await api.post("/free/claim-alias", { claim_code: claimCode.trim() });
      toast.success("Alias claimed into your account!");
      setClaimCode("");
      setClaimDialogOpen(false);
      fetchAliases();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to claim alias";
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setClaiming(false);
    }
  };

  const handleAliasUpdated = (updatedAlias) => {
    setAliases((prev) => prev.map((a) =>
      a.alias_id === updatedAlias.alias_id ? { ...a, ...updatedAlias } : a
    ));
    setSelectedAlias((prev) => prev ? { ...prev, ...updatedAlias } : prev);
  };

  return (
    <div className="h-full flex page-enter" data-testid="alias-page">
      {/* Sidebar — Groups */}
      <div className="w-52 shrink-0 border-r border-[#E5E5E5] p-3 hidden sm:block overflow-y-auto" data-testid="alias-sidebar">
        <InboxGroups
          activeGroup={activeGroup}
          onGroupChange={(gid) => { setActiveGroup(gid); setLoading(true); }}
          totalAliases={totalAliasCount}
          refreshKey={groupsVersion}
        />
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
      {/* Header */}
      <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-[#E5E5E5] flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-medium text-[#111111]" data-testid="alias-title">Aliases</h2>
          <p className="text-sm text-zinc-500 mt-0.5">{aliases.length} alias{aliases.length !== 1 ? "es" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={claimDialogOpen} onOpenChange={setClaimDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="claim-alias-button">
                <Key size={14} className="mr-1.5" /> Claim
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="claim-alias-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">Claim Free Alias</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <p className="text-sm text-zinc-500">Enter the claim code from a free alias to add it to your account.</p>
                <div className="space-y-2">
                  <Label>Claim Code</Label>
                  <Input
                    placeholder="XXXX-XXXX-XXXX"
                    value={claimCode}
                    onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
                    className="font-mono tracking-wider"
                    data-testid="claim-alias-code-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setClaimDialogOpen(false)}>Cancel</Button>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleClaimAlias} disabled={claiming} data-testid="claim-alias-submit">
                  {claiming ? "Claiming..." : "Claim Alias"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" size="sm" data-testid="create-alias-button">
                <Plus size={16} className="mr-1.5" /> New Alias
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-alias-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">Create New Alias</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Label</Label>
                  <Input
                    placeholder="e.g., Shopping, Newsletter"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    data-testid="alias-label-input"
                  />
                  <AliasSenderPreview
                    name={newLabel}
                    address=""
                    testId="create-alias-sender-preview"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description <span className="text-zinc-500">(optional)</span></Label>
                  <Input
                    placeholder="What's this alias for?"
                    value={newDesc}
                    onChange={(e) => setNewDesc(e.target.value)}
                    data-testid="alias-desc-input"
                  />
                </div>
                <p className="text-xs text-zinc-500 font-mono">A random anonymous address will be generated automatically.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid="alias-create-submit"
                >
                  {creating ? "Creating..." : "Create Alias"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Alias List */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : aliases.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center" data-testid="empty-aliases">
            <div className="w-16 h-16 rounded-2xl bg-[#F5F5F5] flex items-center justify-center mb-4">
              <AtSign size={28} className="text-zinc-500" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-lg font-medium text-[#111111] mb-1">No aliases yet</h3>
            <p className="text-sm text-zinc-500 mb-6">Create your first anonymous email alias.</p>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setDialogOpen(true)} data-testid="empty-create-alias">
              <Plus size={16} className="mr-2" /> Create Alias
            </Button>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={aliases.map(a => a.alias_id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y divide-[#2A2A2A]">
                {aliases.map((alias) => (
                  <SortableAliasRow
                    key={alias.alias_id}
                    alias={alias}
                    copiedId={copiedId}
                    onSelect={() => setSelectedAlias(alias)}
                    onCopy={(e) => { e.stopPropagation(); copyAddress(alias.alias_address, alias.alias_id); }}
                    onToggle={() => handleToggle(alias)}
                    onDelete={() => handleDelete(alias.alias_id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </ScrollArea>

      {/* Alias Detail Sheet */}
      <Sheet open={!!selectedAlias} onOpenChange={(open) => { if (!open) setSelectedAlias(null); }}>
        <SheetContent className="w-full sm:max-w-md p-0 flex flex-col" data-testid="alias-detail-sheet">
          {selectedAlias && (
            <AliasDetailPanel
              alias={selectedAlias}
              onUpdate={handleAliasUpdated}
              onDelete={() => { handleDelete(selectedAlias.alias_id); setSelectedAlias(null); }}
              onClose={() => setSelectedAlias(null)}
              onCopy={(addr, id) => copyAddress(addr, id)}
              copiedId={copiedId}
              onGroupChanged={() => { setGroupsVersion((v) => v + 1); fetchAliases(); }}
            />
          )}
        </SheetContent>
      </Sheet>
      </div>
    </div>
  );
}


function AliasDetailPanel({ alias, onUpdate, onDelete, onClose, onCopy, copiedId, onGroupChanged }) {
  const [forwardTo, setForwardTo] = useState(alias.forward_to || "");
  const [forwardEnabled, setForwardEnabled] = useState(alias.forward_enabled || false);
  const [label, setLabel] = useState(alias.label || "");
  const [description, setDescription] = useState(alias.description || "");
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [groups, setGroups] = useState([]);
  const [currentGroupId, setCurrentGroupId] = useState(alias.group_id || "ungrouped");
  const [assigningGroup, setAssigningGroup] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Fetch groups for the assignment selector
  useEffect(() => {
    api.get("/inbox-groups").then(({ data }) => setGroups(data.groups || [])).catch(() => {});
  }, []);

  // Sync state when alias changes
  useEffect(() => {
    setForwardTo(alias.forward_to || "");
    setForwardEnabled(alias.forward_enabled || false);
    setLabel(alias.label || "");
    setDescription(alias.description || "");
    setEmailError("");
    setCurrentGroupId(alias.group_id || "ungrouped");
  }, [alias.alias_id, alias.forward_to, alias.forward_enabled, alias.label, alias.description, alias.group_id]);

  const handleGroupAssign = async (groupId) => {
    const prev = currentGroupId;
    setCurrentGroupId(groupId); // optimistic
    setAssigningGroup(true);
    try {
      const target = groupId === "ungrouped" ? "none" : groupId;
      await api.put(`/inbox-groups/${target}/assign`, { alias_id: alias.alias_id });
      const groupName = groupId === "ungrouped" ? null : groups.find((g) => g.group_id === groupId)?.name;
      onUpdate({ ...alias, group_id: groupId === "ungrouped" ? null : groupId });
      onGroupChanged?.();
      toast.success(groupName ? `Moved to ${groupName}` : "Removed from group");
    } catch {
      setCurrentGroupId(prev); // rollback
      toast.error("Failed to assign group");
    } finally {
      setAssigningGroup(false);
    }
  };

  const validateEmail = (value) => {
    if (!value) {
      setEmailError("");
      return true;
    }
    if (!isValidEmail(value)) {
      setEmailError("Invalid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleSave = async () => {
    if (forwardTo && !isValidEmail(forwardTo)) {
      setEmailError("Invalid email address");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        label: label || "Unnamed Alias",
        description,
        forward_to: forwardTo || null,
        forward_enabled: forwardTo ? forwardEnabled : false,
      };
      const { data } = await api.put(`/aliases/${alias.alias_id}`, payload);
      onUpdate(data);
      toast.success("Alias updated");
    } catch {
      toast.error("Failed to update alias");
    } finally {
      setSaving(false);
    }
  };

  const handleForwardToggle = async (checked) => {
    if (checked && !forwardTo) {
      toast.error("Set a forwarding address first");
      return;
    }
    if (checked && !isValidEmail(forwardTo)) {
      setEmailError("Invalid email address");
      return;
    }
    setForwardEnabled(checked);
    // Save immediately on toggle
    try {
      const { data } = await api.put(`/aliases/${alias.alias_id}`, {
        forward_enabled: checked,
      });
      onUpdate(data);
      toast.success(checked ? "Forwarding enabled" : "Forwarding disabled");
    } catch {
      setForwardEnabled(!checked);
      toast.error("Failed to update forwarding");
    }
  };

  return (
    <div className="flex flex-col h-full">
      <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E5E5E5]">
        <SheetTitle className="font-heading text-lg">Inbox Settings</SheetTitle>
        <SheetDescription className="sr-only">Configure inbox forwarding and details</SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1">
        <div className="px-6 py-5 pb-8 space-y-6">
          {/* Alias Address */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Email address</Label>
            <div className="flex items-center gap-2">
              <span className="flex-1 text-sm text-[#555555] bg-[#F5F5F5] px-3 py-2 rounded-lg border border-[#E5E5E5] truncate" data-testid="detail-alias-address">
                {alias.alias_address}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCopy(alias.alias_address, alias.alias_id)}
                className="shrink-0"
                data-testid="detail-copy-alias"
              >
                {copiedId === alias.alias_id ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Label & Description */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-[#555555]">Inbox name</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Shopping, Newsletter"
                className="h-10"
                data-testid="detail-label-input"
              />
              <AliasSenderPreview
                name={label}
                address={alias.alias_address}
                testId="detail-alias-sender-preview"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-[#555555]">Description</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's this alias for?"
                className="h-10"
                data-testid="detail-desc-input"
              />
            </div>
          </div>

          <Separator />

          {/* Inbox Group */}
          <div className="space-y-3" data-testid="group-assign-section">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
                <FolderOpen size={16} className="text-violet-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-[#111111]">Inbox Group</h4>
                <p className="text-xs text-zinc-500">Organize this alias into a group</p>
              </div>
            </div>
            <Select
              value={currentGroupId}
              onValueChange={handleGroupAssign}
              disabled={assigningGroup}
            >
              <SelectTrigger className="h-10" data-testid="group-assign-trigger">
                <SelectValue placeholder="No group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ungrouped">
                  <span className="text-zinc-500">No group</span>
                </SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.group_id} value={g.group_id}>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${GROUP_DOT_COLOR[g.color] || GROUP_DOT_COLOR.zinc}`} />
                      {g.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Forwarding Section */}
          <div className="space-y-4" data-testid="forwarding-section">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Forward size={16} className="text-emerald-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-[#111111]">Email Forwarding</h4>
                <p className="text-xs text-zinc-500">Forward incoming emails to your real address</p>
              </div>
              <Switch
                checked={forwardEnabled}
                onCheckedChange={handleForwardToggle}
                data-testid="forward-enabled-toggle"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-[#555555]">Forward to</Label>
              <Input
                type="email"
                value={forwardTo}
                onChange={(e) => {
                  setForwardTo(e.target.value);
                  if (emailError) validateEmail(e.target.value);
                }}
                onBlur={() => validateEmail(forwardTo)}
                placeholder="your-real@email.com"
                disabled={!forwardEnabled}
                className={`h-10 text-sm ${emailError ? "border-red-300 focus-visible:ring-red-400" : ""} ${!forwardEnabled ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid="forward-to-input"
              />
              {emailError && (
                <p className="text-xs text-red-500 flex items-center gap-1" data-testid="forward-to-error">
                  <AlertCircle size={12} /> {emailError}
                </p>
              )}
              {forwardTo && !emailError && (
                <p className="text-xs text-zinc-500">
                  Emails to <span className="font-medium">{alias.alias_address}</span> will be forwarded here.
                </p>
              )}
            </div>

            {forwardEnabled && forwardTo && !emailError && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="forwarding-active-banner">
                  <Forward size={14} className="text-emerald-600 shrink-0" />
                  <p className="text-xs text-emerald-600">
                    Forwarding active to <span className="font-medium">{forwardTo}</span>
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  disabled={sendingTest}
                  onClick={async () => {
                    setSendingTest(true);
                    try {
                      await api.post("/mail/test-forwarding", { alias_id: alias.alias_id });
                      toast.success(
                        `Test email sent to ${forwardTo}. Check your inbox (also spam/junk).`,
                        { duration: 6000 }
                      );
                    } catch (err) {
                      const detail = err?.response?.data?.detail || "Failed to send test email";
                      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
                    } finally {
                      setSendingTest(false);
                    }
                  }}
                  data-testid="send-test-email-button"
                >
                  {sendingTest
                    ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Sending...</>
                    : <><Send size={14} className="mr-1.5" /> Send test email</>
                  }
                </Button>
              </div>
            )}

            {alias.forward_disabled_reason === "excessive_bounces" && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200" data-testid="forwarding-bounced-banner">
                <AlertCircle size={14} className="text-red-500 shrink-0" />
                <p className="text-xs text-red-600">
                  Forwarding was disabled due to excessive bounces. Update the address and re-enable.
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5]">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Emails</p>
              <p className="text-lg font-medium text-[#111111] mt-1">{alias.email_count || 0}</p>
            </div>
            <div className="p-3 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5]">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Status</p>
              <p className="text-lg font-medium mt-1">
                {alias.is_active
                  ? <span className="text-emerald-600">Active</span>
                  : <span className="text-zinc-500">Inactive</span>
                }
              </p>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Sticky Footer — always visible */}
      <div className="px-6 pt-4 pb-12 border-t border-[#E5E5E5] flex items-center justify-between bg-white sticky bottom-0 z-50 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]" data-testid="alias-detail-footer">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDeleteConfirmOpen(true)}
          className="text-red-600 border-red-200 hover:bg-red-50"
          data-testid="detail-delete-alias"
        >
          <Trash2 size={14} className="mr-1.5" /> Delete alias
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-[#888888]"
            data-testid="detail-cancel-alias"
          >
            Cancel
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
            onClick={handleSave}
            disabled={saving}
            data-testid="detail-save-alias"
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="delete-alias-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium text-[#111111]">Delete this alias?</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500 mt-2 space-y-2">
              <span className="block">This will permanently delete the alias and stop receiving emails. This action cannot be undone.</span>
              <span className="block text-zinc-400 text-xs">All associated emails will no longer be accessible.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              data-testid="delete-alias-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                setDeleteConfirmOpen(false);
                onDelete();
              }}
              data-testid="delete-alias-confirm"
            >
              <Trash2 size={14} className="mr-1.5" /> Delete alias
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
